/**
 * LayoutsDrawer.tsx
 * Drawer pour la sélection de layouts photobooth
 * Extrait de ModernSidebar.tsx
 */
import React, { useCallback } from 'react';
import { useAppStore } from '../../../../services/StateService';
import { createDefaultShape } from '../../../../types/ShapeTypes';
import { logger } from '../../../../utils/ProfessionalLogger';

interface LayoutsDrawerProps {
  onClose: () => void;
}

export const LayoutsDrawer: React.FC<LayoutsDrawerProps> = ({ onClose }) => {
  const addShape = useAppStore(state => state.addShape);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);

  const handleLayoutClick = useCallback(
    (layoutType: 'single' | 'strip3' | 'grid2x2') => {
      logger.info(`[LayoutsDrawer] handleLayoutClick appelé avec layoutType: ${layoutType}`);
      const centerX = templateWidth / 2;
      const centerY = templateHeight / 2;
      const spacing = 20;
      const photoWidth = 200;
      const photoHeight = 150;

      try {
        switch (layoutType) {
          case 'single': {
            const shape = createDefaultShape(
              'photoSpace',
              centerX - photoWidth / 2,
              centerY - photoHeight / 2
            );
            shape.width = photoWidth;
            shape.height = photoHeight;
            shape.photoIndex = 1;
            addShape(shape);
            logger.info(`[LayoutsDrawer] Shape photoSpace ajoutée: ${shape.id}`);
            break;
          }
          case 'strip3': {
            const totalWidth = photoWidth * 3 + spacing * 2;
            const startX = centerX - totalWidth / 2;
            for (let i = 0; i < 3; i++) {
              const shape = createDefaultShape(
                'photoSpace',
                startX + i * (photoWidth + spacing),
                centerY - photoHeight / 2
              );
              shape.width = photoWidth;
              shape.height = photoHeight;
              shape.photoIndex = i + 1;
              addShape(shape);
              logger.info(`[LayoutsDrawer] Shape photoSpace ${i + 1} ajoutée: ${shape.id}`);
            }
            break;
          }
          case 'grid2x2': {
            const totalWidth = photoWidth * 2 + spacing;
            const totalHeight = photoHeight * 2 + spacing;
            const startX = centerX - totalWidth / 2;
            const startY = centerY - totalHeight / 2;
            let index = 1;
            for (let row = 0; row < 2; row++) {
              for (let col = 0; col < 2; col++) {
                const shape = createDefaultShape(
                  'photoSpace',
                  startX + col * (photoWidth + spacing),
                  startY + row * (photoHeight + spacing)
                );
                shape.width = photoWidth;
                shape.height = photoHeight;
                shape.photoIndex = index++;
                addShape(shape);
                logger.info(`[LayoutsDrawer] Shape photoSpace ${index - 1} ajoutée: ${shape.id}`);
              }
            }
            break;
          }
        }
        const store = useAppStore.getState();
        logger.info(`[LayoutsDrawer] Nombre de shapes dans le store: ${store.shapes.length}`);
        onClose();
      } catch (error) {
        logger.error("[LayoutsDrawer] Erreur lors de l'ajout du layout:", error);
      }
    },
    [addShape, templateWidth, templateHeight, onClose]
  );

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Mise en page</h3>
        <button className="drawer-close-btn" onClick={onClose} aria-label="Fermer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="drawer-content">
        <div className="drawer-grid">
          <div
            className="drawer-item"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              logger.info('[LayoutsDrawer] Layout "1 Photo" cliqué');
              handleLayoutClick('single');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                logger.info('[LayoutsDrawer] Layout "1 Photo" activé (clavier)');
                handleLayoutClick('single');
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              pointerEvents: 'auto',
            }}
          >
            <div className="drawer-item-preview layout-preview">
              <div className="layout-single"></div>
            </div>
            <p>1 Photo</p>
          </div>
          <div
            className="drawer-item"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              logger.info('[LayoutsDrawer] Layout "3 Photos Bandelette" cliqué');
              handleLayoutClick('strip3');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                logger.info('[LayoutsDrawer] Layout "3 Photos Bandelette" activé (clavier)');
                handleLayoutClick('strip3');
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              pointerEvents: 'auto',
            }}
          >
            <div className="drawer-item-preview layout-preview">
              <div className="layout-strip">
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
            <p>3 Photos Bandelette</p>
          </div>
          <div
            className="drawer-item"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              logger.info('[LayoutsDrawer] Layout "Grille 2x2" cliqué');
              handleLayoutClick('grid2x2');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                logger.info('[LayoutsDrawer] Layout "Grille 2x2" activé (clavier)');
                handleLayoutClick('grid2x2');
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              pointerEvents: 'auto',
            }}
          >
            <div className="drawer-item-preview layout-preview">
              <div className="layout-grid">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
            <p className="drawer-item-title">Grille 2x2</p>
          </div>
        </div>
      </div>
    </>
  );
};
